import { useState } from 'react';

const qualities = [
  { icon: 'ri-shield-check-line', text: 'All inquiries handled with complete discretion' },
  { icon: 'ri-time-line',         text: 'Response within one business day' },
  { icon: 'ri-user-star-line',    text: 'New clients accepted by referral or direct inquiry' },
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) formData.append(el.name, el.value);
    }

    try {
      await fetch('https://readdy.ai/api/form/d775cfsggtbbtt19rdig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-white py-0">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row min-h-[700px]">

        {/* ── LEFT COLUMN — editorial statement ── */}
        <div className="lg:w-[42%] bg-[#F7F4EF] px-10 md:px-16 lg:px-20 py-20 lg:py-28 flex flex-col justify-between">
          <div>
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-10">
              <div className="w-8 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] text-xs uppercase tracking-[0.3em] font-medium">
                Get in Touch
              </span>
            </div>

            {/* Headline */}
            <h2 className="text-[#1A1A1A] text-3xl md:text-4xl lg:text-[2.6rem] font-light leading-[1.2] tracking-tight mb-8">
              Every great<br />
              partnership begins<br />
              <em className="not-italic text-[#8B7355]">with a conversation.</em>
            </h2>

            {/* Body */}
            <p className="text-[#6B6560] text-sm md:text-base font-light leading-relaxed max-w-sm mb-12">
              Higher Standard Global operates exclusively by referral. If you have been referred to us — or believe your property warrants our attention — we welcome your inquiry.
            </p>

            {/* Quality list */}
            <div className="flex flex-col gap-5 mb-14">
              {qualities.map((q) => (
                <div key={q.icon} className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#E8E4DE] shrink-0 mt-0.5">
                    <i className={`${q.icon} text-sm text-[#C9A961]`} />
                  </div>
                  <p className="text-[#5A5550] text-sm font-light leading-snug pt-1">{q.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom contact detail + quote */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#E8E4DE] shrink-0">
                <i className="ri-mail-line text-sm text-[#C9A961]" />
              </div>
              <a
                href="mailto:info@higherstandardglobal.com"
                className="text-[#3A3530] text-sm hover:text-[#8B7355] transition-colors"
              >
                info@higherstandardglobal.com
              </a>
            </div>

            <div className="pt-8 border-t border-[#E0DDD9]">
              <p className="text-[#9A9590] text-xs font-light leading-relaxed italic">
                &ldquo;We do not advertise or list properties publicly. All client relationships are established through trusted referrals only.&rdquo;
              </p>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN — form ── */}
        <div className="lg:w-[58%] bg-white px-10 md:px-16 lg:px-20 py-20 lg:py-28 flex flex-col justify-center">

          {submitted ? (
            <div className="flex flex-col items-center justify-center text-center py-16 max-w-sm mx-auto">
              <div className="w-16 h-16 flex items-center justify-center rounded-full border border-[#C9A961] mb-8">
                <i className="ri-check-line text-2xl text-[#C9A961]" />
              </div>
              <h3 className="text-[#1A1A1A] text-2xl font-light mb-4">Message Received</h3>
              <p className="text-[#7A7570] text-sm font-light leading-relaxed">
                Thank you for reaching out. A member of our team will review your inquiry and be in touch within one business day.
              </p>
            </div>
          ) : (
            <>
              {/* Form header */}
              <div className="mb-10">
                <h3 className="text-[#1A1A1A] text-xl font-light tracking-tight mb-2">
                  Submit an Inquiry
                </h3>
                <p className="text-[#9A9590] text-sm font-light">
                  All fields marked <span className="text-[#C9A961]">*</span> are required.
                </p>
              </div>

              <form
                data-readdy-form
                id="hsg-contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
              >
                {/* Name + Email row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#9A9590] font-medium">
                      Full Name <span className="text-[#C9A961]">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Your full name"
                      className="bg-[#FAFAF9] border border-[#E8E4DE] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C0BCB8] focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#9A9590] font-medium">
                      Email Address <span className="text-[#C9A961]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="bg-[#FAFAF9] border border-[#E8E4DE] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C0BCB8] focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                </div>

                {/* Service + Referred By row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#9A9590] font-medium">
                      Service of Interest
                    </label>
                    <div className="relative">
                      <select
                        name="service_interest"
                        className="w-full bg-[#FAFAF9] border border-[#E8E4DE] rounded-md px-4 py-3 text-sm text-[#3A3530] focus:outline-none focus:border-[#C9A961] transition-colors appearance-none cursor-pointer"
                      >
                        <option value="">Select a service</option>
                        <option value="Long-Term Rental Management">Long-Term Rental Management</option>
                        <option value="Short-Term Rental Management">Short-Term Rental Management</option>
                        <option value="Vacation Property Management">Vacation Property Management</option>
                        <option value="Tenant Screening">Tenant Screening</option>
                        <option value="Lease Management">Lease Management</option>
                        <option value="Maintenance Coordination">Maintenance Coordination</option>
                        <option value="Full Management Package">Full Management Package</option>
                      </select>
                      <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#9A9590]">
                        <i className="ri-arrow-down-s-line text-base" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#9A9590] font-medium">
                      Referred By
                    </label>
                    <input
                      type="text"
                      name="referred_by"
                      placeholder="Name of your referral"
                      className="bg-[#FAFAF9] border border-[#E8E4DE] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C0BCB8] focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#9A9590] font-medium flex justify-between items-center">
                    <span>Message <span className="text-[#C9A961]">*</span></span>
                    <span className={`normal-case tracking-normal text-xs ${charCount > 450 ? 'text-[#C9A961]' : 'text-[#C0BCB8]'}`}>
                      {charCount} / 500
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your property and what you are looking for in a management partner..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="bg-[#FAFAF9] border border-[#E8E4DE] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#C0BCB8] focus:outline-none focus:border-[#C9A961] transition-colors resize-none leading-relaxed"
                  />
                </div>

                {/* Submit row */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                  <p className="text-[#B0ACA8] text-xs font-light leading-relaxed max-w-xs">
                    Your information is kept strictly confidential and never shared with third parties.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting || charCount > 500}
                    className="inline-flex items-center gap-2.5 bg-[#1A1A1A] hover:bg-[#2C2C2C] text-white font-medium text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-sm" />
                        Sending
                      </>
                    ) : (
                      <>
                        Send Inquiry
                        <i className="ri-arrow-right-line text-sm" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

      </div>
    </section>
  );
}
